import React, { useEffect, useId, useRef, useState } from "react";
import { signInAnonymously } from "firebase/auth";
import {
  collection,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { auth, db } from "../firebase";

const REPORT_REASONS = [
  ["website", "Website does not work"],
  ["phone", "Phone number is wrong"],
  ["address", "Address is wrong"],
  ["closed", "Resource may no longer be open"],
  ["details", "Services or details are inaccurate"],
  ["other", "Something else"],
];

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const MINIMUM_FORM_TIME_MS = 1500;

const ResourceReportDialog = ({ resource, directory, accent = "cyan" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [details, setDetails] = useState("");
  const [company, setCompany] = useState("");
  const [openedAt, setOpenedAt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const titleId = useId();
  const detailsId = useId();
  const closeButtonRef = useRef(null);
  const triggerButtonRef = useRef(null);

  const accentClasses =
    accent === "blue"
      ? "text-blue-300 hover:text-blue-200 focus:ring-blue-400"
      : "text-cyan-400 hover:text-cyan-300 focus:ring-cyan-400";

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const triggerButton = triggerButtonRef.current;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
      triggerButton?.focus();
    };
  }, [isOpen]);

  const openDialog = () => {
    setReasons([]);
    setDetails("");
    setCompany("");
    setError("");
    setSuccess(false);
    setOpenedAt(Date.now());
    setIsOpen(true);
  };

  const toggleReason = (reason) => {
    setReasons((current) =>
      current.includes(reason)
        ? current.filter((item) => item !== reason)
        : [...current, reason]
    );
  };

  const submitReport = async (event) => {
    event.preventDefault();
    setError("");

    if (company) {
      setSuccess(true);
      return;
    }

    if (Date.now() - openedAt < MINIMUM_FORM_TIME_MS) {
      setError("Please take a moment to review the information before sending.");
      return;
    }

    if (reasons.length === 0) {
      setError("Choose at least one item that needs attention.");
      return;
    }

    const duplicateKey = `resource-report:${directory}:${resource.id}:${[...reasons]
      .sort()
      .join(",")}`;
    const priorSubmission = Number(localStorage.getItem(duplicateKey) || 0);
    if (priorSubmission && Date.now() - priorSubmission < DAY_IN_MS) {
      setError("You already sent this report today. Thank you for helping us keep it accurate.");
      return;
    }

    setSubmitting(true);

    try {
      const credential = auth.currentUser
        ? { user: auth.currentUser }
        : await signInAnonymously(auth);
      const reporterUid = credential.user.uid;
      const rateLimitRef = doc(db, "resourceReportRateLimits", reporterUid);
      const reportRef = doc(collection(db, "resourceReports"));

      await runTransaction(db, async (transaction) => {
        const rateLimitSnapshot = await transaction.get(rateLimitRef);
        const rateLimit = rateLimitSnapshot.exists()
          ? rateLimitSnapshot.data()
          : null;
        const windowStartedAt = rateLimit?.windowStartedAt?.toMillis?.() || 0;
        const shouldReset = !windowStartedAt || Date.now() - windowStartedAt >= DAY_IN_MS;
        const nextCount = shouldReset ? 1 : Number(rateLimit.count || 0) + 1;

        if (nextCount > 5) {
          throw new Error("REPORT_LIMIT");
        }

        transaction.set(rateLimitRef, {
          reporterUid,
          count: nextCount,
          lastReportId: reportRef.id,
          windowStartedAt: shouldReset
            ? serverTimestamp()
            : rateLimit.windowStartedAt,
          lastSubmission: serverTimestamp(),
        });

        transaction.set(reportRef, {
          directory,
          resourceId: resource.id,
          resourceName: resource.name,
          reasons,
          details: details.trim(),
          pagePath: `${window.location.pathname}${window.location.search}`,
          snapshot: {
            address: resource.address || "",
            phone: resource.phone || "",
            website: resource.website || "",
            email: resource.email || "",
          },
          status: "open",
          createdAt: serverTimestamp(),
          reporterUid,
        });
      });

      localStorage.setItem(duplicateKey, String(Date.now()));
      setSuccess(true);
    } catch (submissionError) {
      console.error("Unable to submit resource report:", submissionError);
      if (submissionError.message === "REPORT_LIMIT") {
        setError("You have reached the report limit for today. Please try again tomorrow.");
      } else {
        setError("We could not send the report right now. Please try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        ref={triggerButtonRef}
        type="button"
        onClick={openDialog}
        className={`mt-3 text-xs underline underline-offset-4 rounded focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 ${accentClasses}`}
      >
        Report incorrect information
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4 py-8"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="w-full max-w-lg max-h-full overflow-y-auto rounded-lg border border-gray-600 bg-gray-900 p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id={titleId} className="text-xl font-bold text-white">
                  Report incorrect information
                </h2>
                <p className="mt-1 text-sm text-gray-300">{resource.name}</p>
              </div>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded px-2 py-1 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                aria-label="Close report form"
              >
                ✕
              </button>
            </div>

            {success ? (
              <div className="mt-6" role="status">
                <p className="rounded border border-green-600 bg-green-950 p-4 text-green-200">
                  Thank you. Your report was sent to the site administrator for review.
                </p>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="mt-4 w-full rounded bg-cyan-500 px-4 py-2 font-semibold text-gray-900 hover:bg-cyan-400"
                >
                  Close
                </button>
              </div>
            ) : (
              <form className="mt-6" onSubmit={submitReport}>
                <fieldset>
                  <legend className="text-sm font-semibold text-gray-200">
                    What needs attention?
                  </legend>
                  <div className="mt-3 space-y-2">
                    {REPORT_REASONS.map(([value, label]) => (
                      <label
                        key={value}
                        className="flex cursor-pointer items-start gap-3 rounded border border-gray-700 bg-gray-800 p-3 text-sm text-gray-200 hover:border-gray-500"
                      >
                        <input
                          type="checkbox"
                          checked={reasons.includes(value)}
                          onChange={() => toggleReason(value)}
                          className="mt-0.5 h-4 w-4 accent-cyan-500"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <label htmlFor={detailsId} className="mt-5 block text-sm font-semibold text-gray-200">
                  Additional details (optional)
                </label>
                <textarea
                  id={detailsId}
                  value={details}
                  onChange={(event) => setDetails(event.target.value.slice(0, 500))}
                  rows={4}
                  maxLength={500}
                  className="mt-2 w-full rounded border border-gray-600 bg-gray-800 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none"
                  placeholder="For example: the phone number reaches another organization."
                />
                <p className="mt-1 text-right text-xs text-gray-500">{details.length}/500</p>

                <div className="absolute -left-[10000px]" aria-hidden="true">
                  <label>
                    Company
                    <input
                      type="text"
                      value={company}
                      onChange={(event) => setCompany(event.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </label>
                </div>

                {error && (
                  <p className="mt-4 rounded border border-red-700 bg-red-950 p-3 text-sm text-red-200" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-5 w-full rounded bg-cyan-500 px-4 py-3 font-semibold text-gray-900 hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send report"}
                </button>
                <p className="mt-3 text-xs leading-relaxed text-gray-500">
                  Please do not include private medical or personal information. Reports are reviewed before directory details are changed.
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ResourceReportDialog;
